'use client';

import { useState, useCallback } from 'react';
import {
  CompanyInfo,
  ContactInfo,
  DEFAULT_EXTENSIONS,
  DomainExtension,
  DomainSuggestion,
  EmailPrefix,
  SimulatorState,
} from './lib/types';
import { generateDomains } from './lib/domain-generator';
import StepIndicator from './components/StepIndicator';
import Step1Landing from './components/Step1Landing';
import Step2CompanyInfo from './components/Step2CompanyInfo';
import Step3DomainResults from './components/Step3DomainResults';
import Step4EmailSimulator from './components/Step4EmailSimulator';
import Step5Summary from './components/Step5Summary';
import Step6Contact from './components/Step6Contact';
import Step7Payment from './components/Step7Payment';
import Step8Confirmation from './components/Step8Confirmation';

const initialState: SimulatorState = {
  step: 1,
  company: { name: '', acronym: '', sector: '' },
  domains: [],
  selectedDomain: '',
  selectedEmails: [],
  contact: { fullName: '', email: '', phone: '', country: '' },
  paymentChoice: null,
  paymentStatus: null,
};

export default function Home() {
  const [state, setState] = useState<SimulatorState>(initialState);

  const setStep = useCallback((step: number) => {
    setState((prev) => ({ ...prev, step }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const setCompany = useCallback((company: CompanyInfo) => {
    setState((prev) => ({ ...prev, company }));
  }, []);

  const setDomains = useCallback((domains: DomainSuggestion[]) => {
    setState((prev) => ({ ...prev, domains }));
  }, []);

  const setSelectedDomain = useCallback((selectedDomain: string) => {
    setState((prev) => ({ ...prev, selectedDomain }));
  }, []);

  const toggleEmail = useCallback((prefix: EmailPrefix) => {
    setState((prev) => ({
      ...prev,
      selectedEmails: prev.selectedEmails.includes(prefix)
        ? prev.selectedEmails.filter((e) => e !== prefix)
        : [...prev.selectedEmails, prefix],
    }));
  }, []);

  const setContact = useCallback((contact: ContactInfo) => {
    setState((prev) => ({ ...prev, contact }));
  }, []);

  // Generate domains when moving from step 2 to 3
  const handleGenerateDomains = useCallback(() => {
    const generated = generateDomains(state.company.name, state.company.acronym);
    setState((prev) => ({
      ...prev,
      domains: generated,
      selectedDomain: '',
      step: 3,
    }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [state.company.name, state.company.acronym]);

  const handleRestart = useCallback(() => {
    setState(initialState);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const showStepIndicator = state.step >= 2 && state.step <= 7;

  return (
    <div className="min-h-screen">
      {showStepIndicator && (
        <div className="sticky top-0 bg-background/80 backdrop-blur-sm z-10 pt-4 pb-2 px-4">
          <StepIndicator currentStep={state.step} />
        </div>
      )}

      <div className={`px-4 pb-16 ${showStepIndicator ? 'pt-4' : ''}`}>
        {state.step === 1 && (
          <Step1Landing onNext={() => setStep(2)} />
        )}

        {state.step === 2 && (
          <Step2CompanyInfo
            company={state.company}
            onChange={setCompany}
            onNext={handleGenerateDomains}
            onBack={() => setStep(1)}
          />
        )}

        {state.step === 3 && (
          <Step3DomainResults
            domains={state.domains}
            selectedDomain={state.selectedDomain}
            onSelect={setSelectedDomain}
            onDomainsUpdate={setDomains}
            onNext={() => setStep(4)}
            onBack={() => setStep(2)}
          />
        )}

        {state.step === 4 && (
          <Step4EmailSimulator
            domain={state.selectedDomain}
            selectedEmails={state.selectedEmails}
            onToggle={toggleEmail}
            onNext={() => setStep(5)}
            onBack={() => setStep(3)}
          />
        )}

        {state.step === 5 && (
          <Step5Summary
            domain={state.selectedDomain}
            selectedEmails={state.selectedEmails}
            onNext={() => setStep(6)}
            onBack={() => setStep(4)}
          />
        )}

        {state.step === 6 && (
          <Step6Contact
            contact={state.contact}
            onChange={setContact}
            onNext={() => setStep(7)}
            onBack={() => setStep(5)}
          />
        )}

        {state.step === 7 && (
          <Step7Payment
            domain={state.selectedDomain}
            selectedEmails={state.selectedEmails}
            contact={state.contact}
            onPaymentSuccess={() => {
              setState((prev) => ({
                ...prev,
                paymentChoice: 'pay',
                paymentStatus: 'success',
                step: 8,
              }));
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onCallbackRequest={() => {
              setState((prev) => ({
                ...prev,
                paymentChoice: 'callback',
                step: 8,
              }));
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onBack={() => setStep(6)}
          />
        )}

        {state.step === 8 && (
          <Step8Confirmation
            domain={state.selectedDomain}
            selectedEmails={state.selectedEmails}
            paymentChoice={state.paymentChoice || 'callback'}
            onRestart={handleRestart}
          />
        )}
      </div>
    </div>
  );
}